// ---------------------------------------------------------------------------
// Digital Cemetery — Seeded Galaxy Generator
//
// Generates the ENTIRE galaxy deterministically from a fixed seed. Every
// device renders the identical galaxy, and any star can be recomputed from
// its index at any time — which is why we never need to store unclaimed
// stars in Firestore.
//
// ⚠️  FROZEN ARTIFACT: once star claiming ships (Phase 3), GALAXY_SEED and
// every function in this file that affects star positions must NEVER change.
// Claimed star IDs map to positions via this exact math. Visual upgrades
// must be additive (new layers, new files) — never a re-shuffle.
// ---------------------------------------------------------------------------

export const GALAXY_SEED = 'digital-cemetery-v1';

export const GALAXY_CONFIG = Object.freeze({
  arms: 4,             // spiral arms
  maxRadius: 100,      // galactic disk radius (world units)
  coreRadius: 14,      // radius of the central bulge
  twist: 4.4,          // how far the arms wind around the core (radians)
  armSpread: 0.30,     // angular scatter around each arm's spine
  thickness: 5,        // vertical thickness of the disk
});

// Poetic region names, one per spiral arm (order matters — frozen).
export const REGIONS = ['Lyra Arm', 'Orion Reach', 'Cygnus Drift', 'Vela Shore'];
export const CORE_REGION = 'The Luminous Heart';
export const HALO_REGION = 'The Quiet Halo';

// --- Deterministic PRNG -----------------------------------------------------

function xmur3(str) {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return function () {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    return (h ^= h >>> 16) >>> 0;
  };
}

export function mulberry32(a) {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const BASE_SEED = xmur3(GALAXY_SEED)();

// Cheap symmetric pseudo-gaussian in [-1, 1]
function gauss(rng) {
  return (rng() + rng() + rng()) * (2 / 3) - 1;
}

// --- Star colors ------------------------------------------------------------
// Rough stellar temperature classes, warm-biased toward the core.
const DISK_PALETTE = [
  [0.62, 0.75, 1.0],  // blue-white (young arm stars)
  [0.75, 0.85, 1.0],  // pale blue
  [1.0, 1.0, 1.0],    // white
  [1.0, 0.95, 0.8],   // warm white
  [1.0, 0.85, 0.6],   // yellow
  [1.0, 0.7, 0.45],   // orange
];
const CORE_PALETTE = [
  [1.0, 0.95, 0.8],
  [1.0, 0.88, 0.65],
  [1.0, 0.78, 0.5],
  [1.0, 0.65, 0.4],
];

function pickColor(rng, palette) {
  const c = palette[Math.floor(rng() * palette.length)];
  const jitter = 0.92 + rng() * 0.16;
  return [c[0] * jitter, c[1] * jitter, c[2] * jitter];
}

// --- Single-star generation (the frozen core of the system) -----------------
// Every attribute of star i derives from exactly this sequence of rng draws.

export function starFromIndex(i, cfg = GALAXY_CONFIG) {
  const rng = mulberry32((BASE_SEED ^ Math.imul(i + 1, 0x9e3779b1)) >>> 0);
  const roll = rng();

  let x, y, z, color, region;

  if (roll < 0.16) {
    // --- Central bulge ---
    const r = cfg.coreRadius * Math.pow(rng(), 0.5);
    const theta = rng() * Math.PI * 2;
    const phi = Math.acos(2 * rng() - 1);
    x = r * Math.sin(phi) * Math.cos(theta);
    z = r * Math.sin(phi) * Math.sin(theta);
    y = r * Math.cos(phi) * 0.55;
    color = pickColor(rng, CORE_PALETTE);
    region = CORE_REGION;
  } else if (roll < 0.19) {
    // --- Sparse halo ---
    const r = cfg.maxRadius * (0.35 + 0.75 * rng());
    const theta = rng() * Math.PI * 2;
    const phi = Math.acos(2 * rng() - 1);
    x = r * Math.sin(phi) * Math.cos(theta);
    z = r * Math.sin(phi) * Math.sin(theta);
    y = r * Math.cos(phi) * 0.45;
    color = pickColor(rng, DISK_PALETTE);
    region = HALO_REGION;
  } else {
    // --- Spiral arm disk star ---
    const arm = Math.floor(rng() * cfg.arms);
    const t = Math.pow(rng(), 0.62); // density falls off outward
    const r = cfg.coreRadius * 0.4 + t * (cfg.maxRadius - cfg.coreRadius * 0.4);
    const armBase = (arm / cfg.arms) * Math.PI * 2;
    const windAngle = (r / cfg.maxRadius) * cfg.twist;
    const scatter = gauss(rng) * cfg.armSpread * (0.6 + (r / cfg.maxRadius) * 0.9);
    const angle = armBase + windAngle + scatter;
    x = Math.cos(angle) * r + gauss(rng) * 1.6;
    z = Math.sin(angle) * r + gauss(rng) * 1.6;
    const flatten = 1 - 0.55 * (r / cfg.maxRadius);
    y = gauss(rng) * cfg.thickness * flatten * 0.5;
    color = pickColor(rng, DISK_PALETTE);
    region = REGIONS[arm];
  }

  const size = 0.7 + Math.pow(rng(), 5) * 3.2; // mostly small, a few brilliant

  return { id: `star_${i}`, x, y, z, color, size, region };
}

// --- Bulk generation for rendering ------------------------------------------

export function generateGalaxy(count, cfg = GALAXY_CONFIG) {
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const sizes = new Float32Array(count);

  for (let i = 0; i < count; i++) {
    const s = starFromIndex(i, cfg);
    positions[i * 3] = s.x;
    positions[i * 3 + 1] = s.y;
    positions[i * 3 + 2] = s.z;
    colors[i * 3] = s.color[0];
    colors[i * 3 + 1] = s.color[1];
    colors[i * 3 + 2] = s.color[2];
    sizes[i] = s.size;
  }

  return { positions, colors, sizes };
}

// --- Region label anchors (display only, safe to tweak) ---------------------

export function regionLabelPositions(cfg = GALAXY_CONFIG) {
  const labelRadius = cfg.maxRadius * 0.58;
  return REGIONS.map((name, arm) => {
    const armBase = (arm / cfg.arms) * Math.PI * 2;
    const angle = armBase + (labelRadius / cfg.maxRadius) * cfg.twist;
    return {
      name,
      x: Math.cos(angle) * labelRadius,
      y: 7,
      z: Math.sin(angle) * labelRadius,
    };
  });
}
